import {
  AuthUser,
  ForgotPasswordValues,
  PhoneVerificationPurpose,
  SigninFormValues,
  SignupFormValues,
} from "./types";
import { MOSIDA_API_BASE_URL } from "../../config/apiUrls";

const API_BASE_URL = MOSIDA_API_BASE_URL;

type JsonObject = Record<string, any>;

const buildUrl = (path: string): string => {
  const trimmedBase = API_BASE_URL.replace(/\/+$/, "");
  const trimmedPath = path.replace(/^\/+/, "");
  return `${trimmedBase}/${trimmedPath}`;
};

const toAuthUser = (payload: JsonObject, fallbackEmail?: string): AuthUser => {
  const data = payload?.data ?? payload?.user ?? payload ?? {};
  const fullName = [
    data?.firstName ?? data?.first_name,
    data?.lastName ?? data?.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const id =
    data?.id ??
    data?._id ??
    data?.userId ??
    data?.uid ??
    data?.email ??
    fallbackEmail ??
    "anonymous-user";

  const phoneRaw = data?.phone_number ?? data?.phone ?? null;

  return {
    id: String(id),
    email: data?.email ?? fallbackEmail ?? null,
    name:
      data?.name ??
      data?.fullName ??
      (fullName || fallbackEmail || null),
    phone_number:
      phoneRaw === undefined || phoneRaw === null
        ? null
        : String(phoneRaw),
  };
};

const parseErrorMessage = (payload: JsonObject, fallback: string): string => {
  return (
    payload?.message ||
    payload?.error ||
    payload?.errors?.[0]?.message ||
    fallback
  );
};

const fetchProfile = async (accessToken: string): Promise<JsonObject | null> => {
  const response = await fetch(buildUrl("/auth/user/profile"), {
    method: "GET",
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const raw = await response.text();
  let json: JsonObject = {};
  try {
    json = raw ? JSON.parse(raw) : {};
  } catch {
    json = {};
  }

  if (!response.ok) {
    throw new Error(parseErrorMessage(json, "Failed to fetch user profile"));
  }

  return json;
};

async function postJson<T>(path: string, body: JsonObject): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(body),
  });

  const raw = await response.text();
  let json: JsonObject = {};
  try {
    json = raw ? JSON.parse(raw) : {};
  } catch {
    json = { message: raw };
  }

  if (!response.ok) {
    throw new Error(parseErrorMessage(json, `Request failed with status ${response.status}`));
  }

  return json as T;
}

async function postJsonWithAuth<T>(
  path: string,
  body: JsonObject,
  accessToken: string
): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  const raw = await response.text();
  let json: JsonObject = {};
  try {
    json = raw ? JSON.parse(raw) : {};
  } catch {
    json = { message: raw };
  }

  if (!response.ok) {
    throw new Error(parseErrorMessage(json, `Request failed with status ${response.status}`));
  }

  return json as T;
}

async function patchJsonWithAuth<T>(
  path: string,
  body: JsonObject,
  accessToken: string
): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  const raw = await response.text();
  let json: JsonObject = {};
  try {
    json = raw ? JSON.parse(raw) : {};
  } catch {
    json = { message: raw };
  }

  if (!response.ok) {
    throw new Error(parseErrorMessage(json, `Request failed with status ${response.status}`));
  }

  return json as T;
}

const readAccessToken = (response: JsonObject): string | null =>
  response?.access_token ??
  response?.data?.access_token ??
  null;

async function finalizeAuthSession(
  response: JsonObject,
  fallbackEmail?: string
): Promise<AuthUser> {
  const accessToken = readAccessToken(response);
  const loginUser = toAuthUser(response, fallbackEmail);

  if (!accessToken) {
    return loginUser;
  }

  try {
    const profileResponse = await fetchProfile(accessToken);
    const profileUser = toAuthUser(profileResponse || {}, fallbackEmail);
    return {
      ...loginUser,
      ...profileUser,
      accessToken,
    };
  } catch {
    return { ...loginUser, accessToken };
  }
}

export async function preSignupCheckPhoneApi(payload: {
  phone_number: string;
}): Promise<JsonObject> {
  return postJson<JsonObject>("/auth/user/pre-signup/check-phone", payload);
}

export async function sendPhoneVerificationApi(
  payload: { phone_number: string; purpose: PhoneVerificationPurpose },
  accessToken?: string | null
): Promise<JsonObject> {
  if (payload.purpose === "change_phone") {
    if (!accessToken) {
      throw new Error("Authentication required to change phone number.");
    }
    return postJsonWithAuth<JsonObject>(
      "/auth/user/phone-verification/send",
      payload,
      accessToken
    );
  }
  return postJson<JsonObject>("/auth/user/phone-verification/send", payload);
}

export async function verifyPhoneVerificationApi(payload: {
  phone_number: string;
  code: string;
}): Promise<JsonObject> {
  return postJson<JsonObject>("/auth/user/phone-verification/verify", payload);
}

export async function loginWithPhoneApi(payload: {
  phone_number: string;
}): Promise<AuthUser> {
  const response = await postJson<JsonObject>("/auth/user/login-with-phone", payload);
  return finalizeAuthSession(response);
}

export async function updateUserProfilePhoneApi(
  phone_number: string,
  accessToken: string
): Promise<JsonObject> {
  return patchJsonWithAuth<JsonObject>(
    "/auth/user/profile",
    { phone_number },
    accessToken
  );
}

export async function fetchUserProfileApi(
  accessToken: string
): Promise<JsonObject> {
  const data = await fetchProfile(accessToken);
  return data ?? {};
}

export type SignupResult = {
  user: AuthUser;
  message: string;
};

export const authService = {
  async signup(
    payload: SignupFormValues,
    options?: { phone_number?: string | null }
  ): Promise<SignupResult> {
    const body: JsonObject = {
      first_name: payload.firstName.trim(),
      last_name: payload.lastName.trim(),
      username: payload.username.trim(),
      email: payload.email.trim().toLowerCase(),
      password: payload.password,
      confirm_password: payload.confirmPassword,
    };
    if (options?.phone_number) {
      body.phone_number = options.phone_number;
    }
    const response = await postJson<JsonObject>("/auth/user/register", body);
    const message =
      (typeof response?.message === "string" && response.message) ||
      (typeof response?.data?.message === "string" && response.data.message) ||
      "Congratulations! 🎉 Your account has been created and verified.";

    // Some backends return a session token directly on register; use it.
    if (readAccessToken(response)) {
      const user = await finalizeAuthSession(response, payload.email);
      return { user, message };
    }

    // Otherwise sign in to obtain a real session. If that fails we must NOT
    // return a tokenless user (which would look "logged in" but break every
    // authenticated call) — surface an actionable error instead.
    try {
      const user = await authService.signin({
        email: payload.email.trim().toLowerCase(),
        password: payload.password,
      });
      return { user, message };
    } catch {
      throw new Error("Your account was created. Please sign in to continue.");
    }
  },

  async signin(payload: SigninFormValues): Promise<AuthUser> {
    const response = await postJson<JsonObject>("/auth/user/login", {
      email: payload.email.trim().toLowerCase(),
      password: payload.password,
    });
    return finalizeAuthSession(response, payload.email);
  },

  async forgotPassword(payload: ForgotPasswordValues): Promise<void> {
    await postJson<JsonObject>("/auth/user/forgot-password", {
      email: payload.email.trim().toLowerCase(),
    });
  },

  async googleAuth(): Promise<void> {
    const authUrl = buildUrl("/auth/user/google");
    window.location.href = authUrl;
  },
};
